import { Slot } from "@radix-ui/react-slot";
import { type ButtonHTMLAttributes, forwardRef, type ReactNode } from "react";
import { type InteractionSoundKind, interactionSoundProps } from "../interaction-sound";
import { cn } from "../lib/cn";

/*
 * Button variants per the 2026-05-12 design system handoff.
 *
 * - primary  · teal-700 fill, white ink, 16px radius, 10px 18px pad,
 *              specific drop shadow. One decisive action per surface.
 * - ghost    · transparent fill, ink-on-paper text, hair border. Used
 *              for secondary actions next to a primary.
 * - plan     · transparent, mist-dark border, teal-700 ink + trailing
 *              arrow. Lower-emphasis "open plan / open detail" link.
 * - link     · plain text only, gray-500 ink, no border. Tertiary
 *              ("skip", "not now").
 * - navy     · navy pill with cream-warm mono caps. Reflection /
 *              commit affordance (Save reflection).
 * - danger   · destructive red for delete / sign out (legacy variant,
 *              kept for backward compatibility with destructive flows).
 *
 * The `onDark` flag flips primary/ghost for use on pressing-tier
 * surfaces (the dark-teal Attention card): primary inverts to
 * cream-warm fill + pressing-ink text, ghost gets a paper-tinted
 * border. The card itself never has to know.
 *
 * Backward compatibility: `secondary` and `outline` from the previous
 * variant set remain as aliases (secondary -> primary, outline ->
 * ghost) so the migration is non-breaking.
 *
 * `unstyled` is the escape hatch for surfaces with their own complete
 * button design (the attention toast's tier-aware splash buttons): the
 * component owns the semantics (element, type, children) while the
 * caller drives every visual via className. Use it only when a named
 * variant genuinely cannot express the treatment.
 */
export type ButtonVariant =
  | "primary"
  | "ghost"
  | "plan"
  | "link"
  | "navy"
  | "danger"
  | "secondary"
  | "outline"
  | "unstyled";
export type ButtonSize = "sm" | "md" | "lg";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  asChild?: boolean;
  loading?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  /**
   * Set true when the button sits on a pressing-tier surface (dark teal
   * Attention card). Flips primary + ghost into their on-dark variants.
   */
  onDark?: boolean;
  /**
   * The interaction kind this button represents, which decides its sound (see
   * the interaction-sound taxonomy). Defaults to "action" (a soft press). Set
   * "dismiss" for a close/cancel button, "commit" for a finalising action, or
   * "none" to silence it. Prefer this over the deprecated `silent` flag.
   */
  soundKind?: InteractionSoundKind;
  /** @deprecated Use `soundKind="none"`. Kept so existing call sites compile. */
  silent?: boolean;
}

const BASE =
  "inline-flex items-center justify-center gap-2 whitespace-nowrap font-sans font-medium tracking-tight [transition:background-color_200ms_var(--ease-out-expo),border-color_200ms_var(--ease-out-expo),color_200ms_var(--ease-out-expo),box-shadow_200ms_var(--ease-out-expo),transform_120ms_var(--ease-out-expo)] select-none disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600";

function variantClass(variant: ButtonVariant, onDark: boolean): string {
  switch (variant) {
    case "secondary":
    case "primary": {
      if (onDark) {
        return "rounded-xl bg-surface-ambient text-[var(--color-attention-surface-3)] hover:bg-surface-elevated active:scale-[0.98]";
      }
      return "rounded-xl bg-teal-700 text-ink-on-brand shadow-button hover:bg-teal-800 active:scale-[0.98]";
    }
    case "outline":
    case "ghost": {
      if (onDark) {
        return "rounded-xl bg-transparent text-[color-mix(in_srgb,var(--color-surface-elevated)_92%,transparent)] border border-[color-mix(in_srgb,var(--color-surface-elevated)_28%,transparent)] hover:bg-[color-mix(in_srgb,var(--color-surface-elevated)_12%,transparent)]";
      }
      return "rounded-xl bg-transparent text-ink border border-border-soft hover:bg-cream";
    }
    case "plan":
      return "rounded-xl bg-transparent text-ink-brand border border-border-strong hover:bg-surface-secondary hover:border-border-primary hover:text-teal-800";
    case "link":
      return "rounded-xl bg-transparent text-ink-tertiary hover:text-ink-primary";
    case "navy":
      return "rounded-full bg-navy text-cream-warm font-sans text-xs font-medium hover:bg-navy-deep";
    case "danger":
      return "rounded-xl bg-attention-urgent-ink text-ink-on-media hover:brightness-110 active:scale-[0.98]";
    case "unstyled":
      return "";
  }
}

const SIZES: Record<ButtonSize, string> = {
  // The handoff spec is a single pad value (10px 18px) for primary buttons;
  // we keep three sizes for callers that need them. md is the canonical.
  sm: "h-9 px-3.5 text-[12.5px]",
  md: "h-10 px-[18px] text-[13px]",
  lg: "h-12 px-7 text-[14px]",
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = "primary",
    size = "md",
    asChild,
    loading,
    leftIcon,
    rightIcon,
    onDark = false,
    soundKind,
    silent = false,
    className,
    children,
    disabled,
    ...rest
  },
  ref,
) {
  const Comp = asChild ? Slot : "button";
  // `unstyled` opts out of BASE + variant + size so the caller's own design
  // (e.g. the attention toast's tier-aware CSS) is not fought by the primitive.
  const classes =
    variant === "unstyled"
      ? cn(className)
      : cn(BASE, variantClass(variant, onDark), SIZES[size], className);
  // The cue is derived from the button's interaction kind. The `unstyled` escape
  // hatch and disabled/loading buttons stay silent; otherwise it defaults to a
  // soft "action" press, overridable per call via `soundKind`.
  const silenced = silent || variant === "unstyled" || disabled || loading;
  const soundProps = interactionSoundProps(silenced ? "none" : (soundKind ?? "action"));
  return (
    <Comp ref={ref} className={classes} disabled={disabled || loading} {...soundProps} {...rest}>
      {loading ? <Spinner /> : leftIcon}
      <span>{children}</span>
      {rightIcon}
      {variant === "plan" ? <span aria-hidden="true">→</span> : null}
    </Comp>
  );
});

function Spinner() {
  return (
    <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.25" strokeWidth="4" />
      <path
        d="M22 12a10 10 0 0 1-10 10"
        stroke="currentColor"
        strokeLinecap="round"
        strokeWidth="4"
      />
    </svg>
  );
}
